// Angular imports
import { Component } from '@angular/core';

// Import marked (for markdown compilation)
import { marked } from 'marked';
import { BehaviorSubject, combineLatest, map, Subject } from 'rxjs';
import { FeaturesEntity, VersionHistory } from 'src/app/types/version-history';

// Modal system imports
import { BaseModalComponent } from '../../modal/base-modal';
import { ModalService } from '../../modal/modal.service';

// Types
export type ChangelogModalArgs = {
    version: string;
    changelog: VersionHistory[];
}

@Component({
    selector: 'changelog-modal',
    templateUrl: './changelog-modal.component.html',
    styleUrls: ['./changelog-modal.component.scss'],
    standalone: false
})
export class ChangelogModalComponent extends BaseModalComponent {
    // Title for modal
    public title = 'Versiehistorie';

    public _html: string = '';

    private _history$ = new BehaviorSubject<VersionHistory[]>([]);
    private _activeVersionHistory$ = new BehaviorSubject<VersionHistory | undefined>(undefined);
    private _activeVersionNr$ = new BehaviorSubject<string | undefined>(undefined);
    public _versionNrs$ = this._history$.pipe(
        map((history) => {
            return history.map(h => h.version);
        })
    )

    private _activeHistory$ = combineLatest([
        this._history$,
        this._activeVersionNr$
    ]).pipe(
        map(([history, activeVersion]) => {
            const h = history.find(f => f.version === activeVersion);

            if(h){
                this._activeVersionHistory$.next(h);
            }

            return h;
        })
    )

    constructor(private modalService: ModalService) {
        super();
    }

    public init({ changelog }: ChangelogModalArgs): void {
        this._activeHistory$.subscribe(async (next) => {
            // Convert version history to HTML string
            // First take latest version
            const html = mapLabelToHtml(next?.features ?? []);
    
            // Compile markdown to html
            const possiblePromise = marked(html);
            this._html = (typeof possiblePromise === 'object' && 'then' in possiblePromise) ? await possiblePromise : possiblePromise;
        });

        // Set history
        this._history$.next(changelog);

        // Set active version
        this._activeVersionNr$.next([...changelog].shift()?.version);
    }
}

function mapLabelToHtml(f: FeaturesEntity[]): string{
    const result: string[] = [];

    // Features
    result.push(`## Features`);
    result.push(...f.filter(l => l.labels.includes('Feature')).map(m => mapTrelloCardToHtml(m)).flat());
    result.push('');

    // Features
    result.push(`## Changes`);
    result.push(...f.filter(l => l.labels.includes('Change')).map(m => mapTrelloCardToHtml(m)).flat());
    result.push('');

    // Features
    result.push(`## Bug fixes`);
    result.push(...f.filter(l => l.labels.includes('Bug')).map(m => mapTrelloCardToHtml(m)).flat());   

    return result.join('\n');
}

function mapTrelloCardToHtml(f: FeaturesEntity): string[]{
    return [
        `<p><b>${f.title}</b><br/>`,
        `${f.description}</p>`
    ];
}
